// SetPassword.tsx — Sprint 124 (ADR-117)
//
// Public route /set-password?token=… . An owner claims their auto-provisioned
// login (or resets a forgotten password) by setting a new password. Wired to
// POST /api/auth/set-password via the api client. Success → toast + redirect to
// login. Expired/used/invalid token → a clear message + a "request a new link"
// action that POSTs /api/auth/request-activation.
import React, { useMemo, useState } from 'react';
import { Card, Form, Input, Button, Alert, Typography } from 'antd';
import { LockOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import { staticApi } from '../../utils/staticApi';
import { atriumTokens } from '../../../../theme/theme';

const { Title } = Typography;

// Client-side strength floor — mirrors the backend's min-8 / max-72 guard.
function passwordProblem(pw: string): string | null {
  if (!pw || pw.length < 8) return 'Password must be at least 8 characters.';
  if (new TextEncoder().encode(pw).length > 72) return 'Password must be at most 72 bytes.';
  return null;
}

export const SetPassword: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') ?? '', [searchParams]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenDead, setTokenDead] = useState(false); // expired/used/invalid → offer new link
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);

  const focusRing = `0 0 0 3px ${atriumTokens.blueS}, 0 0 0 1px ${atriumTokens.blue}`;
  const addFocusRing = (e: React.FocusEvent<HTMLElement>) => {
    e.currentTarget.style.outline = `2px solid ${atriumTokens.blue}`;
    e.currentTarget.style.outlineOffset = '2px';
    e.currentTarget.style.boxShadow = focusRing;
  };

  const handleSubmit = async (values: { password: string; confirm: string }) => {
    setError(null);
    if (!token) {
      setTokenDead(true);
      setError('This link is missing its activation token. Please request a new one.');
      return;
    }
    const problem = passwordProblem(values.password);
    if (problem) { setError(problem); return; }
    if (values.password !== values.confirm) { setError('Passwords do not match.'); return; }

    setSubmitting(true);
    try {
      await apiService.setPassword(token, values.password);
      staticApi.message.success('Password set. You can now sign in.');
      navigate('/login-condo-app/', { replace: true });
    } catch (err: any) {
      const msg = err?.message || 'Could not set your password. Please try again.';
      setError(msg);
      // A 400 from the backend means the token is invalid/expired/used — offer
      // the recovery path. (The backend collapses those cases into one message.)
      setTokenDead(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    if (!resendEmail.trim()) { setError('Enter your email to receive a new link.'); return; }
    setResending(true);
    try {
      await apiService.requestActivation(resendEmail.trim());
      // Neutral message — no enumeration.
      staticApi.message.success('If that email has an account, a new link has been sent.');
    } catch {
      // Even on error we keep the message neutral.
      staticApi.message.success('If that email has an account, a new link has been sent.');
    } finally {
      setResending(false);
    }
  };

  return (
    <main style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: atriumTokens.bg, fontFamily: atriumTokens.fontFamily, padding: '48px 16px',
    }}>
      <Card
        styles={{ body: { padding: 0 } }}
        style={{
          maxWidth: '448px', width: '100%', background: atriumTokens.card,
          boxShadow: atriumTokens.sh2, borderRadius: `${atriumTokens.radiusCard}px`,
          border: `1px solid ${atriumTokens.line}`, fontFamily: atriumTokens.fontFamily, overflow: 'hidden',
        }}
      >
        <div style={{ height: '4px', background: atriumTokens.gradGreeting }} />
        <div style={{ padding: '28px 28px 24px' }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <Title level={1} style={{ color: atriumTokens.ink, margin: 0, fontSize: '28px', fontFamily: atriumTokens.fontFamily }}>
              Set Your Password
            </Title>
            <p style={{ color: atriumTokens.mut, marginTop: 8, fontSize: 14 }}>
              Choose a password to activate your resident portal account.
            </p>
          </div>

          {error && (
            <Alert
              message="Please check the details"
              description={error}
              type={tokenDead ? 'warning' : 'error'}
              showIcon
              style={{ marginBottom: '16px' }}
              closable
              onClose={() => setError(null)}
            />
          )}

          {!tokenDead && (
            <Form form={form} name="set-password" onFinish={handleSubmit} layout="vertical" requiredMark={false}>
              <Form.Item
                name="password"
                label="New Password"
                rules={[{ required: true, message: 'Please enter a password!' }]}
                style={{ marginBottom: '20px' }}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: atriumTokens.mut }} />}
                  placeholder="At least 8 characters"
                  size="large"
                  id="set-password"
                  autoComplete="new-password"
                  onFocus={addFocusRing}
                  onBlur={(e) => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.outline = ''; }}
                  iconRender={(visible) => {
                    const label = visible ? 'Hide password' : 'Show password';
                    const Icon = visible ? EyeOutlined : EyeInvisibleOutlined;
                    return (
                      <span role="button" tabIndex={0} aria-label={label} title={label}
                        style={{ color: atriumTokens.mut, display: 'inline-flex', cursor: 'pointer' }}>
                        <Icon aria-hidden="true" />
                      </span>
                    );
                  }}
                  style={{ borderRadius: `${atriumTokens.radiusControl}px`, height: '48px', fontSize: '16px' }}
                />
              </Form.Item>

              <Form.Item
                name="confirm"
                label="Confirm Password"
                rules={[{ required: true, message: 'Please confirm your password!' }]}
                style={{ marginBottom: '24px' }}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: atriumTokens.mut }} />}
                  placeholder="Re-enter your password"
                  size="large"
                  id="confirm-password"
                  autoComplete="new-password"
                  onFocus={addFocusRing}
                  onBlur={(e) => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.outline = ''; }}
                  style={{ borderRadius: `${atriumTokens.radiusControl}px`, height: '48px', fontSize: '16px' }}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary" htmlType="submit" loading={submitting} size="large" block
                  style={{
                    height: '48px', borderRadius: `${atriumTokens.radiusControl}px`, fontSize: '16px',
                    fontWeight: 600, color: atriumTokens.card, background: atriumTokens.blue, border: 'none',
                    boxShadow: atriumTokens.shc,
                  }}
                >
                  Set Password
                </Button>
              </Form.Item>
            </Form>
          )}

          {tokenDead && (
            <div>
              <p style={{ fontSize: 14, color: atriumTokens.ink, marginBottom: 12 }}>
                This link is invalid or has expired. Enter your email to receive a new activation link.
              </p>
              <Input
                type="email"
                placeholder="you@example.com"
                size="large"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                onFocus={addFocusRing}
                onBlur={(e) => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.outline = ''; }}
                style={{ borderRadius: `${atriumTokens.radiusControl}px`, height: '48px', fontSize: '16px', marginBottom: 16 }}
              />
              <Button
                type="primary" block size="large" loading={resending} onClick={handleResend}
                style={{
                  height: '48px', borderRadius: `${atriumTokens.radiusControl}px`, fontSize: '16px',
                  fontWeight: 600, color: atriumTokens.card, background: atriumTokens.blue, border: 'none',
                  boxShadow: atriumTokens.shc,
                }}
              >
                Request a new link
              </Button>
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: '24px', paddingTop: '16px', borderTop: `1px solid ${atriumTokens.line}` }}>
            <Button type="link" onClick={() => navigate('/login-condo-app/')} style={{ padding: 0, fontSize: 14 }}>
              Back to sign in
            </Button>
          </div>
        </div>
      </Card>
    </main>
  );
};

export default SetPassword;
